/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Payments Service - Super Admin Payments API
 * ============================================================================
 *
 * Service layer for payment records API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams, SpringPageResponse, transformPageResponse } from '@/services/api/types';
import { Payment, PaymentFilterParams, InitiatePaymentResponse } from '@/types/superadmin.types';

/**
 * Payment status result from gateway
 */
export interface PaymentStatusResult {
  transactionId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
  gatewayStatus: string;
  message?: string;
  paidAt?: string;
  metadata?: Record<string, string>;
}

/**
 * Polling status info
 */
export interface PollingStatus {
  enabled: boolean;
  availableGateways: string[];
}

const BASE_URL = '/superadmin/payments';

/**
 * Backend payment response (raw entity fields)
 */
interface BackendPayment {
  id: string;
  orgId: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  status: string;
  description?: string;
  externalReference?: string;
  payerName?: string;
  payerEmail?: string;
  partnerId?: string;
  partnerCommission?: number;
  paidAt?: string;
  createdAt: string;
}

/**
 * Transform backend payment to frontend format
 */
function transformPayment(backend: BackendPayment): Payment {
  return {
    id: backend.id,
    organizationId: backend.orgId,
    organizationName: backend.payerName || backend.description?.split(' - ')[0] || 'Organization',
    amount: Number(backend.amount),
    currency: backend.currency,
    status: backend.status as Payment['status'],
    paymentMethod: backend.paymentMethod,
    reference: backend.externalReference || '',
    invoiceNumber: backend.invoiceNumber,
    description: backend.description || '',
    paidAt: backend.paidAt,
    createdAt: backend.createdAt,
    updatedAt: backend.createdAt, // Use createdAt as fallback
  };
}

const PAYMENTS_STATUS_URL = '/payments';
const SUBSCRIPTION_URL = '/admin/organizations';

/**
 * Payments service for Super Admin
 */
export const paymentsService = {
  // ========================================================================
  // Payment Initiation
  // ========================================================================

  /**
   * Initiate subscription payment for an organization
   * Returns redirect URL to payment gateway checkout
   */
  async initiateSubscriptionPayment(
    orgId: string,
    paymentMethod?: 'ORANGE_MONEY' | 'MTN_MONEY' | 'CREDIT_CARD'
  ): Promise<InitiatePaymentResponse> {
    const params = paymentMethod ? `?paymentMethod=${paymentMethod}` : '';
    const response = await post<ApiResponse<InitiatePaymentResponse>>(
      `${SUBSCRIPTION_URL}/${orgId}/subscription/pay${params}`,
      {}
    );
    return response.data;
  },

  // ========================================================================
  // Payment Status Checking
  // ========================================================================

  /**
   * Check payment status directly with gateway
   * Does not update database - just returns current status
   */
  async checkPaymentStatus(
    gateway: string,
    transactionId: string
  ): Promise<PaymentStatusResult> {
    const response = await get<ApiResponse<PaymentStatusResult>>(
      `${PAYMENTS_STATUS_URL}/status/${gateway}/${transactionId}`
    );
    return response.data;
  },

  /**
   * Poll payment status and update database
   */
  async pollPaymentStatus(transactionId: string): Promise<PaymentStatusResult> {
    const response = await post<ApiResponse<PaymentStatusResult>>(
      `${PAYMENTS_STATUS_URL}/poll/${transactionId}`,
      {}
    );
    return response.data;
  },

  /**
   * Check and update payment status by payment ID.
   * Use this on page load to check if a pending payment has completed.
   */
  async checkPaymentStatusById(paymentId: string): Promise<PaymentStatusResult> {
    const response = await post<ApiResponse<PaymentStatusResult>>(
      `${PAYMENTS_STATUS_URL}/${paymentId}/check-status`,
      {}
    );
    return response.data;
  },

  /**
   * Get polling service status (SuperAdmin only)
   */
  async getPollingStatus(): Promise<PollingStatus> {
    const response = await get<ApiResponse<PollingStatus>>(
      `${PAYMENTS_STATUS_URL}/polling/status`
    );
    return response.data;
  },

  // ========================================================================
  // Payment Records (SuperAdmin)
  // ========================================================================
  /**
   * Get paginated list of payments
   */
  async getPayments(
    params?: PaginationParams & PaymentFilterParams
  ): Promise<PaginatedResponse<Payment>> {
    const response = await get<ApiResponse<SpringPageResponse<BackendPayment>>>(BASE_URL, params);
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformPayment),
    };
  },

  /**
   * Get payment by ID
   */
  async getPayment(id: string): Promise<Payment> {
    const response = await get<ApiResponse<BackendPayment>>(`${BASE_URL}/${id}`);
    return transformPayment(response.data);
  },

  /**
   * Mark payment as paid
   */
  async markAsPaid(id: string, externalReference?: string): Promise<Payment> {
    const response = await post<ApiResponse<BackendPayment>>(`${BASE_URL}/${id}/mark-paid`, { externalReference });
    return transformPayment(response.data);
  },

  /**
   * Mark payment as failed
   */
  async markAsFailed(id: string, reason?: string): Promise<Payment> {
    const response = await post<ApiResponse<BackendPayment>>(`${BASE_URL}/${id}/mark-failed`, { reason });
    return transformPayment(response.data);
  },

  /**
   * Refund payment
   */
  async refundPayment(id: string, reason?: string): Promise<Payment> {
    const response = await post<ApiResponse<BackendPayment>>(`${BASE_URL}/${id}/refund`, { reason });
    return transformPayment(response.data);
  },

  /**
   * Export payments to CSV/Excel
   */
  async exportPayments(params?: PaymentFilterParams): Promise<Blob> {
    const response = await get<Blob>(`${BASE_URL}/export`, {
      ...params,
      responseType: 'blob',
    });
    return response;
  },

  /**
   * Get payment statistics
   */
  async getPaymentStats(): Promise<{
    totalRevenue: number;
    monthlyRevenue: number;
    pendingPayments: number;
    failedPayments: number;
  }> {
    const response = await get<ApiResponse<{
      totalRevenue: number;
      monthlyRevenue: number;
      pendingPayments: number;
      failedPayments: number;
    }>>(`${BASE_URL}/stats`);
    return response.data;
  },
};

export default paymentsService;
